// File: libs/services/client/access-control-api-client/src/client.ts
import axios from 'axios';
import { authApi as idpAuthApi } from '@digiwedge/idp-client';
import { Configuration, ConsentsApi, FeatureFlagsApi, FeaturesApi, FeatureStatsApi, LoginAttemptsApi, PermissionAssignmentsApi, PermissionsApi, RolesApi, SessionsApi, TenantsApi, UserProfilesApi, UserRolesApi, UsersApi, } from './index.js';
/**
 * Import your IDP client to call the refresh endpoint.
 */
/**
 * Base path for your Access Control API.
 * Prefer Vite env in browser, then NX_* mapping, else default.
 */
// Safe reader for Vite-style env without hard-referencing import.meta at parse-time (Jest/CJS)
function readViteEnv(name) {
    try {
        // Use Function to avoid parse-time "import.meta" errors under non-ESM Jest
        const fn = Function(`try{ return (typeof import!=="undefined" && import.meta && import.meta.env && import.meta.env["${name}"]) }catch{ return undefined }`);
        return fn();
    }
    catch {
        return undefined;
    }
}
const basePath = 
// Vite client-side env (preferred in CSR apps)
readViteEnv('VITE_ACCESS_CONTROL_BASE_URL') ||
    // Node-style env (when bundled or mapped via define)
    (typeof process !== 'undefined' && process.env?.['VITE_ACCESS_CONTROL_BASE_URL']) ||
    // Fallback default
    'https://access-control-api.uat.digiwedge.com';
/**
 * 1) Create a custom Axios instance for token attachment & refresh interceptors.
 */
const acAxios = axios.create({
    baseURL: basePath,
});
/**
 * 2) Helpers to retrieve tokens from storage.
 *    Adjust if your code uses different keys or "remember me" logic.
 */
function getStoredAccessToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken') || '';
}
function getStoredRefreshToken() {
    return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken') || '';
}
function getStoredJti() {
    return localStorage.getItem('jti') || sessionStorage.getItem('jti') || '';
}
/**
 * 3) Concurrency management for refresh attempts.
 */
let isRefreshing = false;
let pendingRequests = [];
/**
 * Process any queued requests after refresh finishes.
 */
function processQueue(error, token) {
    console.debug('[AC Axios] processQueue called. Error:', error, 'Token:', token);
    pendingRequests.forEach((callback) => callback(token || undefined));
    pendingRequests = [];
}
/**
 * 4) Attach request interceptor to add `Authorization` header if a token is found.
 */
acAxios.interceptors.request.use((config) => {
    const token = getStoredAccessToken();
    console.debug('[AC Axios] request interceptor:', {
        url: config.url,
        method: config.method,
        token,
    });
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        console.debug('[AC Axios] Attached Authorization header:', config.headers.Authorization);
    }
    else {
        console.warn('[AC Axios] No token found to attach.');
    }
    return config;
}, (error) => {
    console.error('[AC Axios] request interceptor error:', error);
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});
/**
 * 5) Attach response interceptor for handling 401 errors (with concurrency-safe refresh).
 */
acAxios.interceptors.response.use((response) => {
    console.debug('[AC Axios] response interceptor success:', {
        url: response.config.url,
        status: response.status,
    });
    return response;
}, async (error) => {
    console.debug('[AC Axios] response interceptor error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
    });
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
        console.warn('[AC Axios] Received 401. Attempting token refresh...');
        originalRequest._retry = true;
        const refreshToken = getStoredRefreshToken();
        const jti = getStoredJti();
        if (!refreshToken) {
            console.warn('[AC Axios] No refresh token found. Clearing tokens...');
            clearAllTokens();
            return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }
        // If refresh is already in progress, queue this request
        if (isRefreshing) {
            console.debug('[AC Axios] Refresh in progress. Queueing this request...');
            return new Promise((resolve, reject) => {
                pendingRequests.push((newToken) => {
                    if (!newToken) {
                        console.error('[AC Axios] Refresh failed. Rejecting queued request...');
                        return reject(error instanceof Error ? error : new Error(String(error)));
                    }
                    console.debug('[AC Axios] Retrying queued request with new token...');
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    resolve(acAxios(originalRequest));
                });
            });
        }
        // Start a new refresh
        isRefreshing = true;
        console.debug('[AC Axios] Initiating refresh flow...');
        try {
            // -- Real refresh logic using IDP AuthenticationApi --
            console.warn('[AC Axios] Refreshing token via AuthenticationApi.authControllerRefresh...');
            const response = await idpAuthApi.authControllerRefresh(navigator.userAgent, {
                refreshToken,
                jti,
            });
            const { accessToken: newAccessToken, refreshToken: newRefreshToken, jti: newJti, } = response.data;
            console.debug('[AC Axios] Got new tokens from refresh:', {
                newAccessToken,
                newRefreshToken,
                newJti,
            });
            // Store updated tokens (and jti) in local or session storage
            // If you use 'remember me' logic, decide how to store them
            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            localStorage.setItem('jti', newJti);
            console.debug('[AC Axios] Updated tokens in storage.');
            // Resolve queued requests with the new token
            processQueue(null, newAccessToken);
            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            console.debug('[AC Axios] Retrying original request with new token...');
            return acAxios(originalRequest);
        }
        catch (refreshError) {
            console.error('[AC Axios] Error refreshing token:', refreshError);
            processQueue(refreshError, null);
            // Clear tokens from storage
            clearAllTokens();
            return Promise.reject(refreshError instanceof Error ? refreshError : new Error(String(refreshError)));
        }
        finally {
            isRefreshing = false;
            console.debug('[AC Axios] Refresh flow complete. isRefreshing = false');
        }
    }
    console.debug('[AC Axios] Non-401 error or request already retried. Rejecting...');
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});
/** Helper to remove all tokens from local/session storage */
function clearAllTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('jti');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('jti');
}
/**
 * 6) Create a basic Configuration (no 'axios' property).
 */
const config = new Configuration({
    basePath,
});
/**
 * 7) Instantiate your API clients by passing `acAxios` as the third argument
 *    so requests go through your custom interceptors.
 */
export const consentsApi = new ConsentsApi(config, basePath, acAxios);
export const featureFlagsApi = new FeatureFlagsApi(config, basePath, acAxios);
export const featuresApi = new FeaturesApi(config, basePath, acAxios);
export const featureStatsApi = new FeatureStatsApi(config, basePath, acAxios);
export const loginAttemptsApi = new LoginAttemptsApi(config, basePath, acAxios);
export const permissionAssignmentsApi = new PermissionAssignmentsApi(config, basePath, acAxios);
export const permissionsApi = new PermissionsApi(config, basePath, acAxios);
export const rolesApi = new RolesApi(config, basePath, acAxios);
export const sessionsApi = new SessionsApi(config, basePath, acAxios);
export const tenantsApi = new TenantsApi(config, basePath, acAxios);
export const userProfilesApi = new UserProfilesApi(config, basePath, acAxios);
export const userRolesApi = new UserRolesApi(config, basePath, acAxios);
export const usersApi = new UsersApi(config, basePath, acAxios);
//# sourceMappingURL=client.js.map